<template>
    <ValidationErrors :show="showErrors"/>
    <Head v-if="updating === false" title="Create New Project"/>
    <Head v-else :title="form.title"/>
    <Audit v-if="openModal" @close="openModal = false" :open="openModal" :activity_logs="project.activity_logs" />
    <div class="w-full mb-6">
        <div v-if="updating === false">
            <h1 class="my-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                <span v-html="'Create New Project'"></span>
            </h1>
            <ProjectTabs/>
        </div>
        <project-header v-else :project="project">
            <template v-slot:actions>
                <Transition name="page" appear>
                <Link @click="archive" v-if="project.archived_at === null"
                        class="inline-flex my-2 h-10 items-center px-4 py-2 border border-transparent rounded-md font-semibold text-xs text-white uppercase bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                    <archive-icon class="-ml-0.5 mr-2 h-4 w-4"></archive-icon>
                    Archive Project
                </Link>
                <Link @click="unarchive" v-else
                      class="inline-flex my-2 h-10 items-center px-4 py-2 border border-transparent rounded-md font-semibold text-xs text-white uppercase bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                    <archive-icon class="-ml-0.5 mr-2 h-4 w-4"></archive-icon>
                    Unarchive Project
                </Link>
                </Transition>
            </template>
        </project-header>
        <div class="space-y-6 lg:col-span-9">
            <section aria-labelledby="payment-details-heading">
                <form @submit.prevent="submit">
                    <div class="shadow sm:rounded-md sm:overflow-hidden">
                        <div class="bg-white px-4 p-6 space-y-6">
                            <!-- Project Form Start -->
                            <div id="project_form" class="space-y-6">
                                <div class="border-b border-gray-200 pb-2">
                                    <h2 id="payment-details-heading" class="text-lg leading-6 font-bold text-gray-900">Project Information</h2>
                                    <p class="mt-1 text-sm text-gray-500">Enter information for the project below.</p>
                                    <button type="button" @click="openModal = true"
                                        class="my-2 h-10 items-center px-4 py-2 border border-transparent rounded-md font-semibold text-xs text-white uppercase bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                                        Show Changelog
                                    </button>
                                    <div class="rounded-md bg-blue-50 p-4 mt-2" v-if="updating === false">
                                        <div class="flex">
                                            <div class="flex-shrink-0">
                                                <InformationCircleIcon class="h-5 w-5 text-blue-400"
                                                                       aria-hidden="true"/>
                                            </div>
                                            <div class="ml-3 flex-1 md:flex md:justify-between">
                                                <p class="text-sm text-blue-700">Information entered here will be used
                                                    to calculate service costs and provide quick access to available
                                                    suppliers.</p>
                                            </div>
                                        </div>
                                    </div>
                                    <p class="mt-2 w-full text-right text-xs text-gray-500 font-italic">Fields marked
                                        with an asterisk
                                        (<span
                                            class="text-red-500">*</span>) are
                                        required.</p>
                                </div>

                                <div class="grid grid-cols-4 gap-6">
                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="title"
                                               class="block text-sm font-medium text-gray-700 required">Title</label>
                                        <input type="text" v-model="form.title" name="title" id="title"
                                               autocomplete="name"
                                               :class="{'border-red-500': form.errors.title}"
                                               class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="client_id"
                                               class="block text-sm font-medium text-gray-700 required">Client</label>
                                        <v-select class="mt-1" :class="{'border-red-500': form.errors.client_id}" :options="clients" label="name" :reduce="clients => clients.id" v-model="form.organisation_id" :disabled="!client_editable" />
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label class="block text-sm font-medium text-gray-700 required">Office</label>
                                        <select id="project_type_id" v-model="form.office_id" :disabled="invoice_count !== 0"
                                                :class="{'border-red-500': form.errors.office_id}"
                                                class="mt-1 block w-full bg-white border border-gray-300 disabled:bg-gray-200 rounded-md  disabled:cursor-not-allowed shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                            <option value selected v-html="'Select an office'"></option>
                                            <option v-for="office in offices" :value="office.id"
                                                    v-html="office.name"></option>
                                        </select>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700 required">Bank Account</label>
                                        <select :disabled="bank_accounts.length === 0 || invoice_count !==  0" id="bank_account_id"
                                                v-model="form.bank_account_id"
                                                :class="{'border-red-500': form.errors.bank_account_id}"
                                                class="mt-1 block w-full bg-white border border-gray-300 disabled:bg-gray-200 disabled:cursor-not-allowed rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                            <option v-if="bank_accounts.length === 0" value selected>You must select an office
                                                first
                                            </option>
                                            <option v-else value selected v-html="'Select a bank account'"></option>
                                            <option v-for="bank_account in bank_accounts" :value="bank_account.id"
                                                    v-html="bank_account.name + ' ('+ bank_account.currency.code + ' - '+ bank_account.currency.symbol + ')'"></option>
                                        </select>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="passengers"
                                               class="block text-sm font-medium text-gray-700 required">Passengers</label>
                                        <input v-model="form.passengers" type="number"
                                               name="passengers" id="passengers"
                                               autocomplete="passengers"
                                               :class="{'border-red-500': form.errors.passengers}"
                                               class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="starts_at"
                                               class="block text-sm font-medium text-gray-700 required">Project Date</label>
                                        <input v-model="form.starts_at" type="date"
                                               name="starts_at" id="starts_at"
                                               autocomplete="starts_at"
                                               :class="{'border-red-500': form.errors.starts_at}"
                                               class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>

                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="ends_at"
                                               class="block text-sm font-medium text-gray-700 required">End Date</label>
                                        <input v-model="form.ends_at" type="date"
                                               name="ends_at" id="ends_at"
                                               :min="form.starts_at ?? moment().format('YYYY-MM-DD')"
                                               autocomplete="ends_at"
                                               :class="{'border-red-500': form.errors.ends_at}"
                                               class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>

                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label for="enquiry_at"
                                               class="block text-sm font-medium text-gray-700 required">Enquiry
                                            Date</label>
                                        <input v-model="form.enquired_at" type="date"
                                               name="enquiry_at" id="enquiry_at"
                                               autocomplete="enquiry_at"
                                               :class="{'border-red-500': form.errors.enquired_at}"
                                               class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>

                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700 required">Country</label>
                                        <select id="project_type_id" v-model="form.country_id"
                                                :class="{'border-red-500': form.errors.country_id}"
                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                            <option value selected v-html="'Select a country'"></option>
                                            <option v-for="country in countries" :value="country.id"
                                                    v-html="country.name"></option>
                                        </select>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700">Region</label>
                                        <select :disabled="regions.length === 0" id="project_type_id"
                                                v-model="form.region_id"
                                                :class="{'border-red-500': form.errors.region_id}"
                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                            <option v-if="regions.length === 0" value selected>You must select a country
                                                first
                                            </option>
                                            <option v-else value selected v-html="'Select a region'"></option>
                                            <option v-for="region in regions" :value="region.id"
                                                    v-html="region.name"></option>
                                        </select>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700 required">Project
                                            Manager</label>
                                        <select id="project_type_id" v-model="form.user_id"
                                                :class="{'border-red-500': form.errors.user_id}"
                                                :disabled="updating === false"
                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                            <option value selected v-html="'Select a team travel manager'"></option>
                                            <option v-for="user in users" :value="user.id"
                                                    v-html="user.label ?? (user.first_name + ' ' + user.last_name)"></option>
                                        </select>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700 required">Project
                                            Type</label>
                                        <select id="project_type_id" v-model="form.project_type_id"
                                                :class="{'border-red-500': form.errors.project_type_id}"
                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                            <option value selected v-html="'Select a project type'"></option>
                                            <option v-for="project_type in project_types" :value="project_type.id"
                                                    v-html="project_type.name"></option>
                                        </select>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700 required">Calculation Method</label>
                                        <select v-model="form.calculation_method"
                                                :class="{'border-red-500': form.errors.calculation_method}"
                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                            <option value  v-html="'Select a calculation method'"></option>
                                            <option v-for="costing_type in costing_types" :value="costing_type"
                                                    v-html="costing_type.charAt(0).toUpperCase() + costing_type.substring(1)"></option>
                                        </select>
                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700 required">Estimated
                                            Cost</label>
                                        <input v-model="form.estimated_cost" type="text"
                                               name="estimated_cost" id="estimated_cost"
                                               autocomplete="estimated_cost"
                                               :class="{'border-red-500': form.errors.estimated_cost}"
                                               class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>

                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700 required">Estimated
                                            Price</label>
                                        <input v-model="form.estimated_price" type="text"
                                               name="estimated_price" id="estimated_price"
                                               autocomplete="estimated_price"
                                               :class="{'border-red-500': form.errors.estimated_price}"
                                               class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>

                                    </div>

                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700 required">Enquiry From</label>
                                        <select id="project_type_id" v-model="form.enquiry_from"
                                                :class="{'border-red-500': form.errors.enquiry_from}"
                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                            <option value selected v-html="'Select an enquiry type'"></option>
                                            <option v-for="enquiry_type in enquiry_types" :value="enquiry_type"
                                                    v-html="enquiry_type.charAt(0).toUpperCase() + enquiry_type.substring(1)"></option>
                                        </select>
                                    </div>


                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700 required">Commission VAT Rate</label>
                                        <select id="project_type_id" v-model="form.commission_vat_rate_id"
                                                :class="{'border-red-500': form.errors.commission_vat_rate_id}"
                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                            <option value selected v-html="'Select a commission VAT rate'"></option>
                                            <option v-for="vat_rate in vat_rates" :value="vat_rate.id"
                                                    v-html="vat_rate.country.code + ' ' + vat_rate.percentage + '%'"></option>
                                        </select>
                                    </div>


                                    <div class="col-span-4 sm:col-span-2 lg:col-span-1">
                                        <label
                                            class="block text-sm font-medium text-gray-700">Status</label>
                                        <select id="project_type_id" v-model="form.status" :disabled="updating === false"
                                                :class="{'border-red-500': form.errors.status}"
                                                class="mt-1 block w-full bg-white border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm">
                                            <option value selected v-html="'Select a project status'"></option>
                                            <option v-for="status in project_statuses" :value="status"
                                                    v-html="status.charAt(0).toUpperCase() + status.substring(1)"></option>
                                        </select>
                                    </div>

                                    <div class="col-span-4 clear-both">
                                        <label
                                            class="block text-sm font-medium text-gray-700">Description</label>
                                        <div class="mt-1">
                                            <quill-editor content-type="html" v-model:content="form.description"
                                                          :class="{'border-red-500': form.errors.description}"
                                                          class="min-h-[150px] block w-full bg-white border border-gray-300 shadow-sm py-2 px-3"
                                                          theme="snow"></quill-editor>
                                        </div>
                                    </div>

                                    <div class="col-span-4 clear-both">
                                        <label
                                            class="block text-sm font-medium text-gray-700">Notes - Internal Use Only</label>
                                        <div class="mt-1">
                                            <quill-editor content-type="html" v-model:content="form.notes"
                                                          :class="{'border-red-500': form.errors.notes}"
                                                          class="min-h-[150px] mt-1 block w-full bg-white border border-gray-300 shadow-sm py-2 px-3"
                                                          theme="snow"></quill-editor>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                        <div class="px-4 py-3 bg-gray-50 text-right sm:px-6">
                            <button type="submit"
                                    class="bg-amaze-coral border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-700">
                                <span v-if="updating === true">Update</span>
                                <span v-else>Save</span>
                            </button>
                        </div>
                    </div>
                </form>
            </section>
        </div>
    </div>
</template>

<script>
import Authenticated from "@/Layouts/Authenticated.vue";
import {computed, ref} from "vue";
import {Link, Head, usePage, useForm} from "@inertiajs/vue3";
import {ClipboardCopyIcon, PlusSmIcon, UsersIcon, InformationCircleIcon} from "@heroicons/vue/outline"
import ValidationErrors from "@/Components/ValidationErrors";
import ProjectTabs from "@/Components/ProjectTabs";
import ProjectHeader from "@/Components/ProjectHeader";
import {ArchiveIcon} from "@heroicons/vue/outline";
import Audit from "@/Components/Modal/Audit.vue";

import Button from "@/Components/Button.vue";
import moment from "moment";

export default {
    layout: Authenticated,
    components: {
        ProjectHeader,
        ProjectTabs,
        ValidationErrors,
        Link,
        Head,
        ClipboardCopyIcon,
        PlusSmIcon,
        UsersIcon,
        InformationCircleIcon,
        ArchiveIcon,
        Button,
        Audit
    },
    created() {
        this.moment = moment
    },
    setup(props) {

        const project = usePage().props.project ?? null;
        const updating = usePage().props.updating
        const showErrors = ref(false);
        const openModal = ref(false);

        const form = useForm({
            id: updating === true ? project.id : null,
            title: updating === true ? project.title : null,
            organisation_id: updating === true ? project.organisation_id : null,
            office_id: updating === true ? project.office_id : "",
            bank_account_id: updating === true ? project.bank_account_id: "",
            project_type_id: updating === true ? project.project_type_id : "",
            passengers: updating === true ? project.passengers : null,
            starts_at: updating === true ? project.starts_at : null,
            ends_at: updating === true ? project.ends_at : null,
            enquired_at: updating === true ? project.enquired_at : moment().format('YYYY-MM-DD'), // default to today if not specified
            country_id: updating === true ? project.country_id : "",
            region_id: updating === true ? project.region_id : "",
            user_id: updating === true ? project.user_id : usePage().props.auth.user.id,
            commission_type: updating === true ? project.commission_type : "",
            estimated_price: updating === true ? project.estimated_price : null,
            estimated_cost: updating === true ? project.estimated_cost : null,
            status: updating === true ? project.status : "Request",
            notes: updating === true ? project.notes : "",
            description: updating === true ? project.description : "",
            calculation_method: updating === true ? project.calculation_method : "",
            enquiry_from: updating === true ? project.enquiry_from : "",
            commission_vat_rate_id: updating === true ? project.commission_vat_rate_id : "",
        });

        // Project Types
        const project_types = usePage().props.project_types;

        // Countries
        const countries = usePage().props.countries;

        // Regions
        const regions = (updating === true) ? countries.find(country => country.id === form.country_id).regions : [];

        // Offices
        const offices = usePage().props.offices;

        // VAT Rates
        //const vat_rates = usePage().props.vat_rates;
        // first check which country to use
        let vat_rates;
        if (updating === true) {
            var commission_country_id = project.office.commission_country_id > 0 ? project.office.commission_country_id : project.office.address.country_id;
            vat_rates = countries.find(country => country.id === commission_country_id).vat_rates;
        } else {
            vat_rates = [];
        }

        // Enquiry From Types
        const enquiry_types = usePage().props.enquiry_types;

        // Bank Accounts
        const bank_accounts = (updating === true)  ? offices.find(office => office.id === form.office_id).bank_accounts : [];

        // Clients
        const clients = usePage().props.clients;

        // Users
        const users = usePage().props.users;

        // Costing Types
        const costing_types = usePage().props.costing_types;

        // Project Statuses
        const project_statuses = usePage().props.project_statuses;

        // Get the invoice count, used to disable certain fields.
        const invoice_count = usePage().props.invoice_count;

        // Client editable, used to determine whether the client field should be editable or not.
        const client_editable = usePage().props.client_editable;


        return {
            form,
            countries,
            regions,
            project_types,
            clients,
            updating,
            users,
            enquiry_types,
            vat_rates,
            project_statuses,
            showErrors,
            project,
            offices,
            bank_accounts,
            costing_types,
            invoice_count,
            openModal,
            client_editable
        }

    },
    methods: {
        /**
         * Submit the form and perform validation on the request.
         */
        submit() {
            if (this.updating === true) {
                if(this.form.status == 'Cancelled' && this.project.status != 'Cancelled') {
                    this.$swal.fire({
                        title: 'Cancelling a Project',
                        html: "You are about to cancel this project. <br/>Are you sure you'd like to do this?",
                        showCloseButton: true,
                        showCancelButton: true,
                        confirmButtonText: 'Yes',
                        cancelButtonText: 'No'
                    }).then((result) => {
                        if (result.isConfirmed) {
                            this.updateForm();
                        }
                    });
                } else {
                    this.updateForm();
                }
            } else {
                this.form.post(route('frontend.project.store'), {
                    onError: (errors) => {
                        this.showErrors = true;
                    },
                    resetOnSuccess: true,
                    preserveState: false
                });
            }
        },
        updateForm() {
            this.form.post(route('frontend.project.update', [this.form.id]), {
                preserveScroll: false,
                preserveState: false,
                resetOnSuccess: true,
                onError: (errors) => {
                    this.showErrors = true;
                }
            });
        },
        archive() {
            this.$swal.fire({
                title: 'Archive Project',
                html: "You are archiving a project. <br/>Are you sure you'd like to do this?",
                showCloseButton: true,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No'
            })
                .then((result) => {
                    if (result.isConfirmed) {
                        // Delete the country
                        this.$inertia.post(route('frontend.project.archive', this.form.id), {
                            preserveScroll: true,
                            resetOnSuccess: true,
                            preserveState: false
                        });
                    }
                });
        },
        unarchive() {
            this.$swal.fire({
                title: 'Unarchive Project',
                html: "You are unarchiving a project. <br/>Are you sure you'd like to do this?",
                showCloseButton: true,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No'
            })
                .then((result) => {
                    if (result.isConfirmed) {
                        // Delete the country
                        this.$inertia.post(route('frontend.project.unarchive', this.form.id), {
                            preserveState: false
                        });
                    }
                });
        }
    },
    watch: {
        'form.country_id': {
            deep: true,
            handler(id) {
                if (id === "" || (this.countries.find(country => country.id === id) === "undefined" && typeof this.countries.find(country => country.id === id).regions === "undefined")) {
                    this.regions = [];
                } else {
                    this.regions = this.countries.find(country => country.id === id).regions
                }
            }
        },
        'form.office_id': {
            deep: true,
            handler(id) {
                if (id === "" || (this.offices.find(office => office.id === id) === "undefined" && typeof this.offices.find(office => office.id === id).bank_accounts === "undefined")) {
                    this.bank_accounts = [];
                } else {
                    this.bank_accounts = this.offices.find(office => office.id === id).bank_accounts
                }
                if (id === "" || (this.offices.find(office => office.id === id) === "undefined" && typeof this.offices.find(office => office.id === id).address.country.vat_rates === "undefined")) {
                    this.vat_rates = [];
                } else {
                    var selected_office = this.offices.find(office => office.id === id);
                    if (selected_office.commission_country_id > 0) {
                        this.vat_rates = selected_office.commission_country.vat_rates;
                    } else {
                        this.vat_rates = selected_office.address.country.vat_rates;
                    }
                }

            }
        }
    }
}
</script>

